import { createContext, useContext, type ReactElement } from "react";
import * as RadixTabs from "@radix-ui/react-tabs";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twRing } from "@/lib/emotionTw";
import type { TabsContentProps, TabsListProps, TabsProps, TabsTriggerProps } from "./Tabs.types";

type TabsStyleContextValue = {
  variant: NonNullable<TabsProps["variant"]>;
  size: NonNullable<TabsProps["size"]>;
};

const TabsStyleContext = createContext<TabsStyleContextValue>({ variant: "line", size: "md" });

type TabsVariant = NonNullable<TabsProps["variant"]>;
type TabsSize = NonNullable<TabsProps["size"]>;

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const TabsListRoot = styled(RadixTabs.List, {
  label: "AgenticTabs-list",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { variant: TabsVariant } }>(({ ownerState }) => [
  {
    display: "flex",
    gap: ".25rem",
    overflowX: "auto",
    "--tw-bg-opacity": "1",
    // Themable list surface: hosts (e.g. ListPageLayout's tabs band) set
    // --bfrs-tabs-list-bg to blend the strip with their own surface. The
    // fallback is the exact pre-existing surface-card expression.
    backgroundColor: "var(--bfrs-tabs-list-bg, hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1)))"
  },
  ownerState.variant === "line"
    ? {
        borderBottomWidth: "1px",
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
        paddingLeft: "1.25rem",
        paddingRight: "1.25rem"
      }
    : {
        borderRadius: "var(--bfrs-tabs-radius)",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
        padding: ".25rem"
      }
]);

const TabsLineWrap = styled("div", {
  label: "AgenticTabs-lineWrap"
})({
  position: "relative",
  isolation: "isolate"
});

const TabsDivider = styled("span", {
  label: "AgenticTabs-divider"
})({
  pointerEvents: "none",
  position: "absolute",
  bottom: 0,
  zIndex: -1,
  height: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-border) / var(--tw-bg-opacity, 1))",
  left: "calc(-1 * var(--bfrs-tabs-divider-bleed,16px))",
  right: "calc(-1 * var(--bfrs-tabs-divider-bleed,16px))"
});

function TabsList({ className, ...props }: TabsListProps) {
  const { variant } = useContext(TabsStyleContext);

  const list = <TabsListRoot ownerState={{ variant }} className={className} {...props} />;

  if (variant !== "line") {
    return list;
  }

  // The bottom divider should reach the parent's full width, ignoring the parent's
  // horizontal padding. A 1px line bleeds past the list on each side by
  // `--bfrs-tabs-divider-bleed` (default 16px = the default Card padding). It sits
  // behind the list (isolated stacking context) so the list's own border-b draws the
  // line under the tabs and the active tab underline still paints on top, while the
  // overhang extends the line to the parent edges. Only the line bleeds — the list
  // background stays within the content area. Override the variable to match a
  // different parent padding (e.g. 22px for a bordered Card, 24px for a page section).
  return (
    <TabsLineWrap>
      {list}
      <TabsDivider aria-hidden="true" />
    </TabsLineWrap>
  );
}

TabsList.displayName = "Tabs.List";

const triggerSizeStyles: Record<TabsSize, CSSObject> = {
  xs: { minHeight: "38px", paddingLeft: "14px", paddingRight: "14px", fontSize: "13px" },
  sm: { minHeight: "2rem", paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: ".75rem", lineHeight: "1rem" },
  md: { minHeight: "2.5rem", paddingLeft: "14px", paddingRight: "14px", fontSize: "13px" },
  lg: { minHeight: "2.75rem", paddingLeft: "1.25rem", paddingRight: "1.25rem", fontSize: ".875rem", lineHeight: "1.25rem" }
};

const TabsTriggerRoot = styled(RadixTabs.Trigger, {
  label: "AgenticTabs-trigger",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: TabsStyleContextValue }>(({ ownerState }) => [
  {
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    gap: ".5rem",
    whiteSpace: "nowrap",
    borderBottomWidth: "2px",
    borderColor: "transparent",
    fontWeight: 500,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:focus-visible": {
      ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
      "--tw-ring-opacity": "1"
    },
    "&:disabled": {
      pointerEvents: "none",
      opacity: ".5"
    }
  },
  triggerSizeStyles[ownerState.size],
  ownerState.variant === "line"
    ? {
        '&[data-state="active"]': {
          "--tw-border-opacity": "1",
          borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
          fontWeight: 600,
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
        }
      }
    : {
        borderRadius: "var(--bfrs-radius-md)",
        borderBottomWidth: "0px",
        '&[data-state="active"]': {
          "--tw-bg-opacity": "1",
          backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
          fontWeight: 600,
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
          "--tw-shadow": "var(--bfrs-shadow-low)",
          "--tw-shadow-colored": "var(--bfrs-shadow-low)",
          boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
        }
      }
]);

function TabsTrigger({ className, badge, children, ...props }: TabsTriggerProps) {
  const { variant, size } = useContext(TabsStyleContext);

  return (
    <TabsTriggerRoot ownerState={{ variant, size }} className={className} {...props}>
      {children}
      {badge}
    </TabsTriggerRoot>
  );
}

TabsTrigger.displayName = "Tabs.Trigger";

const TabsContentRoot = styled(RadixTabs.Content, {
  label: "AgenticTabs-content"
})({
  paddingTop: "1.25rem",
  outline: "2px solid transparent",
  outlineOffset: "2px"
});

function TabsContent({ className, ...props }: TabsContentProps) {
  return <TabsContentRoot className={className} {...props} />;
}

TabsContent.displayName = "Tabs.Content";

const TabsRootEl = styled(RadixTabs.Root, {
  label: "AgenticTabs-root"
})({
  width: "100%"
});

function TabsRoot({ items, variant = "line", size = "md", className, listClassName, triggerClassName, contentClassName, children, ...props }: TabsProps) {
  return (
    <TabsStyleContext.Provider value={{ variant, size }}>
      <TabsRootEl className={className} {...props}>
        {items ? (
          <>
            <TabsList className={listClassName}>
              {items.map((item) => (
                <TabsTrigger key={item.value} value={item.value} disabled={item.disabled} badge={item.badge} className={triggerClassName}>
                  {item.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {items.map((item) => (
              <TabsContent key={item.value} value={item.value} className={contentClassName}>
                {item.content}
              </TabsContent>
            ))}
          </>
        ) : (
          children
        )}
      </TabsRootEl>
    </TabsStyleContext.Provider>
  );
}

TabsRoot.displayName = "Tabs";

export const Tabs = Object.assign(TabsRoot, {
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent
}) as ((props: TabsProps) => ReactElement) & {
  List: typeof TabsList;
  Trigger: typeof TabsTrigger;
  Content: typeof TabsContent;
};
